// ONE RANKING for the community — used by the page's live search (client) and
// by `innernet_find_skills` (the door), so a person typing "design" on the
// shelf and an ai asking for "design" from inside a chat see the same order.
// Pure and import-free on purpose: it runs in the browser.
//
// A query is split into words; every word must land somewhere in the thing for
// it to match at all (so typing narrows, never widens). Where a word lands
// decides the score: the title beats a tag or the category, which beat the
// description, which beats the author. A title that STARTS with the query wins
// outright. Use counts break ties between equals and never outrank relevance.

export interface Searchable {
  title: string;
  description?: string;
  tags?: string[];
  category?: string;
  author?: string;
  /** anything else worth matching (when_to_load, a host) */
  extra?: string;
  /** use score, a tiebreaker */
  weight?: number;
}

const norm = (s: string) => s.toLowerCase().normalize("NFKD").replace(/[^\p{L}\p{N}]+/gu, " ").trim();

export function termsOf(query: string): string[] {
  return norm(query).split(" ").filter((w) => w.length > 0).slice(0, 8);
}

/** 0 = no match. Higher is better. */
export function scoreOf(item: Searchable, terms: string[]): number {
  if (terms.length === 0) return 1;
  const title = norm(item.title);
  const tags = (item.tags ?? []).map(norm);
  const cat = norm(item.category ?? "");
  const desc = norm(item.description ?? "");
  const author = norm(item.author ?? "");
  const extra = norm(item.extra ?? "");
  let total = 0;
  for (const t of terms) {
    let best = 0;
    if (title === t) best = 12;
    else if (title.startsWith(t) || title.split(" ").some((w) => w.startsWith(t))) best = 9;
    else if (title.includes(t)) best = 7;
    if (best < 8 && (cat === t || tags.includes(t))) best = Math.max(best, 8);
    if (best < 6 && (cat.startsWith(t) || tags.some((g) => g.startsWith(t)))) best = Math.max(best, 6);
    if (best < 4 && desc.split(" ").some((w) => w.startsWith(t))) best = Math.max(best, 4);
    if (best < 3 && desc.includes(t)) best = Math.max(best, 3);
    if (best < 3 && author.includes(t)) best = Math.max(best, 3);
    if (best < 2 && extra.includes(t)) best = Math.max(best, 2);
    if (best === 0) return 0; // every word must land
    total += best;
  }
  if (title.startsWith(terms.join(" "))) total += 6;
  // a whisper from usage: at most +1.5, so it orders equals and nothing else
  return total + Math.min(1.5, Math.log10(1 + (item.weight ?? 0)) * 0.5);
}

export function rank<T>(items: T[], query: string, as: (item: T) => Searchable): { item: T; score: number }[] {
  const terms = termsOf(query);
  if (terms.length === 0) return items.map((item) => ({ item, score: 1 }));
  return items
    .map((item) => ({ item, score: scoreOf(as(item), terms) }))
    .filter((r) => r.score > 0)
    .sort((a, b) => b.score - a.score);
}

const STOP = new Set(["a", "an", "the", "to", "for", "of", "in", "on", "and", "or", "with", "my", "me", "i", "is", "it", "how", "do", "make", "need", "want", "help", "some", "this", "that"]);

/** the forgiving ranking, for a whole sentence from inside a chat ("design a
 *  landing page for my app"): small words are dropped and ANY remaining word
 *  may land, the more the better. The page's live search stays strict (typing
 *  must narrow); an ai's description of a task must not come back empty. */
export function rankLoose<T>(items: T[], query: string, as: (item: T) => Searchable): { item: T; score: number }[] {
  const strict = rank(items, query, as);
  const words = termsOf(query).filter((w) => w.length > 2 && !STOP.has(w));
  if (strict.length >= 3 || words.length === 0) return strict;
  const seen = new Set(strict.map((r) => r.item));
  const loose = items
    .filter((item) => !seen.has(item))
    .map((item) => ({ item, score: words.reduce((sum, w) => sum + scoreOf(as(item), [w]), 0) * 0.5 }))
    .filter((r) => r.score > 0)
    .sort((a, b) => b.score - a.score);
  return [...strict, ...loose];
}
